import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, Stat } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash, Receipt, CheckCircle, XCircle, MoneyWavy, ArrowsClockwise,
  Printer, DownloadSimple, Paperclip, Eye, FloppyDisk,
} from "@phosphor-icons/react";
import { fmtMoney, CURRENCIES } from "@/lib/currency";

const STATUS_TABS = [
  { key: "all",         label: "All" },
  { key: "draft",       label: "Drafts" },
  { key: "approved",    label: "Awaiting Payment" },
  { key: "paid",        label: "Paid" },
  { key: "rejected",    label: "Rejected" },
];

function ClaimForm({ open, onClose, activeCompany, employees, accounts, editing, onSaved, formatApiError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [employeeId, setEmployeeId] = useState("");
  const [claimDate, setClaimDate] = useState(today);
  const [purpose, setPurpose] = useState("");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setEmployeeId(editing.employee_id);
      setClaimDate(editing.claim_date);
      setPurpose(editing.business_purpose || "");
      setLines((editing.lines || []).map((l) => ({
        date: l.date, gl_account_id: l.gl_account_id, description: l.description,
        amount: String(l.amount), currency: l.currency, image_file_id: l.image_file_id || null,
        _preview: l.image?.url || null,
      })));
    } else {
      setEmployeeId("");
      setClaimDate(today);
      setPurpose("");
      setLines([blankLine(activeCompany?.currency || "GBP", today)]);
    }
    // eslint-disable-next-line
  }, [open, editing]);

  function blankLine(cur, dt) {
    return { date: dt, gl_account_id: "", description: "", amount: "", currency: cur, image_file_id: null, _preview: null };
  }
  const addLine = () => setLines([...lines, blankLine(activeCompany.currency, claimDate)]);
  const updLine = (i, patch) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const uploadImage = async (i, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/companies/${activeCompany.id}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updLine(i, { image_file_id: data.id, _preview: URL.createObjectURL(file) });
      toast.success("Image attached");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!employeeId) return toast.error("Select an employee");
    if (!lines.length) return toast.error("Add at least one line");
    for (const l of lines) {
      if (!l.gl_account_id) return toast.error("Every line needs a GL account");
      if (!l.description) return toast.error("Every line needs a description");
      if (!(parseFloat(l.amount) > 0)) return toast.error("Every line needs a positive amount");
    }
    setBusy(true);
    try {
      const payload = {
        employee_id: employeeId, claim_date: claimDate, business_purpose: purpose,
        lines: lines.map(({ _preview, ...l }) => ({ ...l, amount: parseFloat(l.amount) })),
      };
      if (editing) {
        await api.patch(`/companies/${activeCompany.id}/expenses/${editing.id}`,
          { business_purpose: purpose, claim_date: claimDate, lines: payload.lines });
        toast.success("Claim updated");
      } else {
        await api.post(`/companies/${activeCompany.id}/expenses`, payload);
        toast.success("Claim created as draft");
      }
      onClose(); onSaved();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-lg max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {editing ? `Edit claim ${editing.claim_number}` : "New Expense Claim"}
          </DialogTitle>
          <DialogDescription>
            All lines convert to <b>{activeCompany.currency}</b> using stored FX rates.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-medium">Employee</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid="claim-employee" className="rounded-md mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.filter((e) => e.active).map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.employee_number} — {e.name} {e.surname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Claim date</Label>
              <Input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} data-testid="claim-date" required className="rounded-md mt-1" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs font-medium">Total ({activeCompany.currency})</Label>
              <div className="mt-1 rounded-md border border-border bg-muted h-10 flex items-center px-3 num font-semibold">
                {fmtMoney(total, activeCompany.currency)}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Business purpose</Label>
            <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="claim-purpose" required className="rounded-md mt-1" placeholder="Why was this money spent on behalf of the company?" rows={2} />
          </div>

          <div className="qb-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold">Lines ({lines.length})</div>
              <Button type="button" onClick={addLine} size="sm" variant="outline" className="rounded-md" data-testid="claim-add-line">
                <Plus size={14} className="mr-1" /> Add line
              </Button>
            </div>
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={i} className="border border-border rounded-md p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start" data-testid={`claim-line-${i}`}>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Date</Label>
                    <Input type="date" value={l.date} onChange={(e) => updLine(i, { date: e.target.value })} className="mt-1 rounded-md h-9 text-xs" />
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">GL account</Label>
                    <Select value={l.gl_account_id} onValueChange={(v) => updLine(i, { gl_account_id: v })}>
                      <SelectTrigger className="mt-1 rounded-md h-9 text-xs" data-testid={`claim-line-gl-${i}`}><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {accounts.filter((a) => a.type === "expense" && a.is_active !== false).map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">Description</Label>
                    <Input value={l.description} onChange={(e) => updLine(i, { description: e.target.value })} className="mt-1 rounded-md h-9 text-xs" data-testid={`claim-line-desc-${i}`} placeholder="e.g. Client dinner" />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">CCY</Label>
                    <Select value={l.currency} onValueChange={(v) => updLine(i, { currency: v })}>
                      <SelectTrigger className="mt-1 rounded-md h-9 text-xs num"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => <SelectItem key={c} value={c} className="num">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Amount</Label>
                    <Input type="number" step="0.01" min="0" value={l.amount} onChange={(e) => updLine(i, { amount: e.target.value })} className="mt-1 rounded-md h-9 text-xs num text-right" data-testid={`claim-line-amt-${i}`} />
                  </div>
                  <div className="md:col-span-1 flex flex-col items-center gap-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Image</Label>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadImage(i, e.target.files[0])} data-testid={`claim-line-img-${i}`} />
                      <div className={`w-9 h-9 rounded-md border ${l.image_file_id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"} flex items-center justify-center hover:bg-primary/10 hover:text-primary transition`} title={l.image_file_id ? "Replace image" : "Attach image"}>
                        <Paperclip size={14} weight={l.image_file_id ? "fill" : "regular"} />
                      </div>
                    </label>
                  </div>
                  <div className="md:col-span-12 flex justify-end">
                    <Button type="button" size="sm" variant="ghost" onClick={() => rmLine(i)} className="rounded-md h-7 text-destructive" data-testid={`claim-line-rm-${i}`}><Trash size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="claim-save" className="rounded-md">
              {busy ? "Saving…" : editing ? "Save changes" : "Create draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    draft: { c: "text-muted-foreground", label: "Draft" },
    approved: { c: "text-amber-700", label: "Awaiting Payment" },
    paid: { c: "text-primary", label: "Paid" },
    rejected: { c: "text-destructive", label: "Rejected" },
  }[status] || { c: "text-muted-foreground", label: status };
  return <span className={`tag-badge ${cfg.c}`} data-testid={`claim-status-${status}`}>{cfg.label}</span>;
}

export default function ExpensesPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [empFilter, setEmpFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [claimOpen, setClaimOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payAcct, setPayAcct] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [detail, setDetail] = useState(null);
  const printRef = useRef(null);

  const loadAll = async () => {
    if (!activeCompany) return;
    const cid = activeCompany.id;
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (empFilter !== "all") params.set("employee_id", empFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const [c, e, a] = await Promise.all([
      api.get(`/companies/${cid}/expenses?${params}`),
      api.get(`/companies/${cid}/employees`),
      api.get(`/companies/${cid}/accounts`),
    ]);
    setClaims(c.data);
    setEmployees(e.data);
    setAccounts(a.data);
    setBanks(a.data.filter((x) => x.type === "asset" && /^1[0-2]/.test(x.code)));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [activeCompany?.id, statusFilter, empFilter, dateFrom, dateTo]);

  const approve = async (c) => {
    if (!window.confirm(`Approve claim ${c.claim_number}? This posts to the ledger:\n  Dr Expense accounts\n  Cr 2200 Expenses Payable`)) return;
    try {
      await api.post(`/companies/${activeCompany.id}/expenses/${c.id}/approve`);
      toast.success(`${c.claim_number} approved and posted`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const openPay = (c) => {
    setPayTarget(c);
    setPayAcct(banks[0]?.id || "");
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayOpen(true);
  };
  const doPay = async () => {
    if (!payAcct) return toast.error("Choose a bank account");
    try {
      await api.post(`/companies/${activeCompany.id}/expenses/${payTarget.id}/pay`, {
        bank_account_id: payAcct, date: payDate,
      });
      toast.success(`${payTarget.claim_number} paid`);
      setPayOpen(false); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const reject = async (c) => {
    const note = window.prompt("Rejection reason?");
    if (note === null) return;
    try {
      await api.post(`/companies/${activeCompany.id}/expenses/${c.id}/reject`, { note });
      toast.success("Rejected"); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete draft ${c.claim_number}?`)) return;
    try {
      await api.delete(`/companies/${activeCompany.id}/expenses/${c.id}`);
      toast.success("Deleted"); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openEdit = (c) => { setEditing(c); setClaimOpen(true); };
  const openNew = () => { setEditing(null); setClaimOpen(true); };

  const exportCsv = () => {
    const header = ["Claim #", "Date", "Employee", "Purpose", "Status", `Total (${activeCompany.currency})`];
    const rows = claims.map((c) => [
      c.claim_number, c.claim_date, `${c.employee?.employee_number} ${c.employee?.name} ${c.employee?.surname}`,
      (c.business_purpose || "").replace(/[\r\n,]/g, " "), c.status, c.total_base_amount,
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `expenses_${activeCompany.name.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const doPrint = () => { window.print(); };

  const totals = useMemo(() => {
    const t = { count: claims.length, outstanding: 0, paid: 0, drafts: 0 };
    for (const c of claims) {
      if (c.status === "approved") t.outstanding += c.total_base_amount;
      else if (c.status === "paid") t.paid += c.total_base_amount;
      else if (c.status === "draft") t.drafts += c.total_base_amount;
    }
    return t;
  }, [claims]);

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  return (
    <>
      <PageHeader
        eyebrow="Expenses"
        title="Expense Claims"
        description="Draft → Approve → Pay. Multi-currency lines convert to your base currency using FX rates."
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={exportCsv} data-testid="claim-export" className="rounded-md"><DownloadSimple size={14} className="mr-2" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={doPrint} data-testid="claim-print" className="rounded-md"><Printer size={14} className="mr-2" /> Print</Button>
            <Button size="sm" onClick={openNew} data-testid="claim-new" className="rounded-md"><Receipt size={14} className="mr-2" /> New Claim</Button>
          </div>
        }
      />

      <PageBody>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total claims" value={totals.count} testId="stat-total" />
          <Stat label="Awaiting payment" value={fmtMoney(totals.outstanding, activeCompany.currency)} tone="negative" testId="stat-outstanding" icon={MoneyWavy} />
          <Stat label="Paid" value={fmtMoney(totals.paid, activeCompany.currency)} tone="positive" testId="stat-paid" icon={CheckCircle} />
          <Stat label="Drafts" value={fmtMoney(totals.drafts, activeCompany.currency)} testId="stat-drafts" />
        </div>

        <div className="qb-card p-4 flex flex-wrap gap-3 items-end">
          <div className="flex gap-1 flex-wrap">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                data-testid={`filter-${t.key}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                  statusFilter === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
                }`}
              >{t.label}</button>
            ))}
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Employee</Label>
            <Select value={empFilter} onValueChange={setEmpFilter}>
              <SelectTrigger data-testid="filter-employee" className="rounded-md h-9 w-56 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_number} — {e.name} {e.surname}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" className="rounded-md h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" className="rounded-md h-9 mt-1" />
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setStatusFilter("all"); setEmpFilter("all"); setDateFrom(""); setDateTo(""); }} className="rounded-md">
            <ArrowsClockwise size={14} className="mr-1" /> Reset
          </Button>
        </div>

        <div className="qb-card overflow-hidden" ref={printRef}>
          <table className="w-full qb-table" data-testid="claims-table">
            <thead>
              <tr>
                <th>Claim #</th>
                <th>Date</th>
                <th>Employee</th>
                <th>Purpose</th>
                <th className="text-right">Lines</th>
                <th className="text-right">Total ({activeCompany.currency})</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} data-testid={`claim-row-${c.id}`}>
                  <td className="num font-medium">{c.claim_number}</td>
                  <td>{c.claim_date}</td>
                  <td>{c.employee?.employee_number} — {c.employee?.name} {c.employee?.surname}</td>
                  <td className="text-muted-foreground max-w-xs truncate" title={c.business_purpose}>{c.business_purpose}</td>
                  <td className="text-right num">{(c.lines || []).length}</td>
                  <td className="text-right num font-semibold">{fmtMoney(c.total_base_amount, activeCompany.currency)}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(c)} className="rounded-md h-8" data-testid={`claim-view-${c.id}`}><Eye size={14} /></Button>
                    {c.status === "draft" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="rounded-md h-8" data-testid={`claim-edit-${c.id}`}><FloppyDisk size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => approve(c)} className="rounded-md h-8 text-primary" data-testid={`claim-approve-${c.id}`} title="Approve & post"><CheckCircle size={14} weight="fill" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => reject(c)} className="rounded-md h-8 text-destructive" data-testid={`claim-reject-${c.id}`} title="Reject"><XCircle size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => del(c)} className="rounded-md h-8 text-destructive" data-testid={`claim-del-${c.id}`}><Trash size={14} /></Button>
                      </>
                    )}
                    {c.status === "approved" && (
                      <Button size="sm" onClick={() => openPay(c)} className="rounded-md h-8 gap-1 ml-1" data-testid={`claim-pay-${c.id}`}>
                        <MoneyWavy size={14} weight="fill" /> Pay
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!claims.length && (
                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No claims match the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>

      <ClaimForm
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        activeCompany={activeCompany}
        employees={employees}
        accounts={accounts}
        editing={editing}
        onSaved={loadAll}
        formatApiError={formatApiError}
      />

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Pay claim {payTarget?.claim_number}</DialogTitle>
            <DialogDescription>Posts: Dr Expenses Payable · Cr Bank account. Amount {fmtMoney(payTarget?.total_base_amount || 0, activeCompany.currency)}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium">Bank account</Label>
              <Select value={payAcct} onValueChange={setPayAcct}>
                <SelectTrigger data-testid="pay-account" className="rounded-md mt-1"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>
                  {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Payment date</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-date" className="rounded-md mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doPay} data-testid="pay-confirm" className="rounded-md">Confirm payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail viewer */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="rounded-lg max-w-3xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">{detail.claim_number}</DialogTitle>
                <DialogDescription>{detail.employee?.name} {detail.employee?.surname} ({detail.employee?.employee_number}) · {detail.claim_date}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="qb-card p-3">
                  <div className="overline mb-1">Business purpose</div>
                  <div>{detail.business_purpose}</div>
                </div>
                <div className="qb-card overflow-hidden">
                  <table className="w-full qb-table">
                    <thead><tr><th>Date</th><th>GL</th><th>Description</th><th className="text-right">Amount</th><th className="text-right">Base</th><th>Img</th></tr></thead>
                    <tbody>
                      {(detail.lines || []).map((l) => (
                        <tr key={l.id}>
                          <td className="num">{l.date}</td>
                          <td className="num text-xs">{l.gl_account?.code} — {l.gl_account?.name}</td>
                          <td>{l.description}</td>
                          <td className="text-right num">{fmtMoney(l.amount, l.currency)}</td>
                          <td className="text-right num text-muted-foreground">{fmtMoney(l.amount_base, detail.currency)}</td>
                          <td>
                            {l.image ? (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${activeCompany.id}/files/${l.image.id}/download`} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">View</a>
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-right font-semibold text-lg num">
                  Total: {fmtMoney(detail.total_base_amount, detail.currency)}
                </div>
                <div className="qb-card p-3">
                  <div className="overline mb-2">Audit trail</div>
                  <ul className="text-xs space-y-1">
                    {(detail.audit || []).map((a, i) => (
                      <li key={i} className="flex justify-between">
                        <span><b className="capitalize">{a.action}</b> — {a.note}</span>
                        <span className="text-muted-foreground num">{a.at?.slice(0, 19).replace("T", " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
